import { assetReferenceSchema, type AssetReference } from '@fotoboek/domain';
import { assetBlobStore } from '../storage/blobStore';

const SUPPORTED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);
const THUMBNAIL_MAX_DIM = 480;

export type ImportedPhoto = {
  asset: AssetReference;
  objectUrl: string;
  thumbnailUrl: string;
};

export type ImportError = { fileName: string; reason: string };

export type ImportPhotosResult = {
  imported: ImportedPhoto[];
  errors: ImportError[];
};

async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function makeThumbnail(bitmap: ImageBitmap, mimeType: string): Promise<Blob> {
  const scale = Math.min(1, THUMBNAIL_MAX_DIM / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Kan geen 2D-canvascontext aanmaken.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  const outputMime = mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Thumbnail-generatie mislukt.'))),
      outputMime,
      0.85,
    );
  });
}

export async function importPhotoFiles(
  projectId: string,
  files: File[],
): Promise<ImportPhotosResult> {
  const imported: ImportedPhoto[] = [];
  const errors: ImportError[] = [];

  for (const file of files) {
    if (!SUPPORTED_MIME.has(file.type)) {
      errors.push({ fileName: file.name, reason: 'Bestandstype wordt niet ondersteund.' });
      continue;
    }

    try {
      const buffer = await file.arrayBuffer();
      const checksum = await sha256Hex(buffer);
      const originalBlob = new Blob([buffer], { type: file.type });

      let bitmap: ImageBitmap;
      try {
        bitmap = await createImageBitmap(originalBlob);
      } catch {
        errors.push({
          fileName: file.name,
          reason: 'Kan deze afbeelding niet decoderen in deze browser.',
        });
        continue;
      }

      const assetId = crypto.randomUUID();
      const thumbnailBlob = await makeThumbnail(bitmap, file.type);
      const widthPx = bitmap.width;
      const heightPx = bitmap.height;
      bitmap.close();

      await assetBlobStore.put(projectId, assetId, 'original', originalBlob);
      await assetBlobStore.put(projectId, assetId, 'thumbnail', thumbnailBlob);

      const asset = assetReferenceSchema.parse({
        id: assetId,
        originalFileName: file.name,
        mimeType: file.type,
        byteSize: file.size,
        widthPx,
        heightPx,
        exifOrientation: 1,
        importedAt: new Date().toISOString(),
        checksumSha256: checksum,
        syncStatus: 'local-only',
        useCount: 0,
      });

      imported.push({
        asset,
        objectUrl: URL.createObjectURL(originalBlob),
        thumbnailUrl: URL.createObjectURL(thumbnailBlob),
      });
    } catch (err) {
      errors.push({
        fileName: file.name,
        reason: err instanceof Error ? err.message : 'Onbekende fout.',
      });
    }
  }

  return { imported, errors };
}

export async function removePhotoAsset(projectId: string, assetId: string): Promise<void> {
  await assetBlobStore.removeAllForAsset(projectId, assetId);
}

export async function loadAssetObjectUrl(
  projectId: string,
  assetId: string,
  kind: 'original' | 'thumbnail',
): Promise<string | null> {
  const blob = await assetBlobStore.get(projectId, assetId, kind);
  return blob ? URL.createObjectURL(blob) : null;
}
